import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useForm } from 'react-hook-form';
import { Quiz } from '@/types/quiz';

interface QuizDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<Quiz, 'id' | 'created_at' | 'updated_at'>) => void;
  quiz?: Quiz | null;
}

interface QuizFormData {
  title: string;
  description: string;
  difficulty: number;
  points_reward: number;
  time_limit?: number;
  attempts_allowed: number;
  is_active: boolean;
}

export const QuizDialog = ({ isOpen, onClose, onSubmit, quiz }: QuizDialogProps) => {
  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<QuizFormData>({
    defaultValues: {
      title: quiz?.title || '',
      description: quiz?.description || '',
      difficulty: quiz?.difficulty || 1,
      points_reward: quiz?.points_reward || 100,
      time_limit: quiz?.time_limit || undefined,
      attempts_allowed: quiz?.attempts_allowed || 3,
      is_active: quiz?.is_active ?? true,
    }
  });

  React.useEffect(() => {
    if (quiz) {
      reset({
        title: quiz.title,
        description: quiz.description || '',
        difficulty: quiz.difficulty,
        points_reward: quiz.points_reward,
        time_limit: quiz.time_limit || undefined,
        attempts_allowed: quiz.attempts_allowed,
        is_active: quiz.is_active,
      });
    } else {
      reset({
        title: '',
        description: '',
        difficulty: 1,
        points_reward: 100,
        time_limit: undefined,
        attempts_allowed: 3,
        is_active: true,
      });
    }
  }, [quiz, reset]);

  const onFormSubmit = (data: QuizFormData) => {
    onSubmit({
      program_id: quiz?.program_id || '', // Use existing program_id when editing, empty string for new quizzes
      ...data,
      time_limit: data.time_limit || undefined, // Convert empty string to undefined
    });
  };

  const watchedIsActive = watch('is_active');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {quiz ? 'Edit Quiz' : 'Create Quiz'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="title">Quiz Title</Label>
              <Input
                id="title"
                {...register('title', { required: 'Title is required' })}
                placeholder="Enter quiz title"
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>
              )}
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Enter quiz description (optional)"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="difficulty">Difficulty Level</Label>
              <Select
                value={watch('difficulty')?.toString()}
                onValueChange={(value) => setValue('difficulty', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">⭐ Beginner</SelectItem>
                  <SelectItem value="2">⭐⭐ Easy</SelectItem>
                  <SelectItem value="3">⭐⭐⭐ Medium</SelectItem>
                  <SelectItem value="4">⭐⭐⭐⭐ Hard</SelectItem>
                  <SelectItem value="5">⭐⭐⭐⭐⭐ Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="points_reward">Points Reward</Label>
              <Input
                id="points_reward"
                type="number"
                {...register('points_reward', { 
                  required: 'Points reward is required',
                  min: { value: 1, message: 'Points must be at least 1' }
                })}
                placeholder="100"
              />
              {errors.points_reward && (
                <p className="text-sm text-red-600 mt-1">{errors.points_reward.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="time_limit">Time Limit (minutes)</Label>
              <Input
                id="time_limit"
                type="number"
                {...register('time_limit', {
                  min: { value: 1, message: 'Time limit must be at least 1 minute' }
                })}
                placeholder="Optional"
              />
              {errors.time_limit && (
                <p className="text-sm text-red-600 mt-1">{errors.time_limit.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="attempts_allowed">Attempts Allowed</Label>
              <Input
                id="attempts_allowed"
                type="number"
                {...register('attempts_allowed', { 
                  required: 'Attempts allowed is required',
                  min: { value: 1, message: 'Must allow at least 1 attempt' }
                })}
                placeholder="3"
              />
              {errors.attempts_allowed && (
                <p className="text-sm text-red-600 mt-1">{errors.attempts_allowed.message}</p>
              )}
            </div>

            <div className="col-span-2 flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={watchedIsActive}
                onCheckedChange={(checked) => setValue('is_active', checked)}
              />
              <Label htmlFor="is_active">Quiz is active</Label>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {quiz ? 'Update Quiz' : 'Create Quiz'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};